"use client";

/**
 * The market record, as a track you move along rather than a column you scroll past.
 *
 * WHY HORIZONTAL. A vertical list of eight dated entries is a list; the dates are just labels down
 * the side and nothing about the layout says "two years passed". Laid along a track, the passage of
 * time is the axis you are moving on, and the entries have an order you can feel. It is also simply
 * better to use: the whole record is one gesture away instead of six screens of scrolling.
 *
 * It runs NEWEST FIRST. The data is authored oldest-first, because that is the order the argument
 * reads in, but somebody landing on this page without touching anything should see where the story
 * has got to — not a 2024 entry about somebody else. So the two entries that are ours are the ones
 * in view at rest, and reading back through the track is reading back through the market.
 *
 * WHAT I TOOK FROM THE REFERENCE AND WHAT I DID NOT. The visual grammar is the good part — a date
 * above a marker, a spine running between markers, a drop line to the text. Kept.
 *
 * The interaction is not. That implementation scrubs the whole track from `mousemove`: put the
 * pointer anywhere over the strip and the content flies to a position derived from the cursor's x.
 * It looks impressive for a second and then it fights you — you cannot rest the pointer over an
 * entry to read it, because being there is what moved it. There is no keyboard path at all, and
 * `cursor: pointer` on a strip that is not clickable promises something that never happens.
 *
 * So the movement here is only ever asked for:
 *   - wheel and trackpad, which already work on any scroll container;
 *   - press and drag, with a grab cursor that says so and a 4px threshold so a click on a card is
 *     still a click;
 *   - previous / next buttons, which disable themselves at each end;
 *   - arrow, Home and End keys, because the strip is focusable — the reference has no keyboard
 *     route to entries that are off-screen, which puts its content out of reach entirely for
 *     anyone not using a mouse.
 *
 * Scroll snapping is `proximity`, not `mandatory`: mandatory fights a reader who wants to sit
 * between two cards, and there is no reason to make that a fight.
 *
 * COMPLETE AT REST AND WITHOUT JAVASCRIPT. Every entry is in the HTML and the strip is a plain
 * overflow container, so before hydration — and for a crawler, and in a thumbnail — the whole
 * record is present and scrollable. The script only adds the conveniences. Nothing here starts at
 * opacity 0 waiting to be revealed.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export type Milestone = {
  when: string;
  what: string;
  detail: string;
  source: string;
  /** Ours rather than somebody else's. Marked in the card, not just coloured. */
  us?: boolean;
};

export function MilestoneTimeline({ items }: { items: Milestone[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(el.scrollLeft >= el.scrollWidth - el.clientWidth - 2);
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  /* One card plus its gap, so a press of the button lands on the next entry rather than on an
     arbitrary number of pixels. */
  const step = useCallback((dir: 1 | -1) => {
    const el = ref.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>(".tl-item");
    const by = card ? card.offsetWidth + 48 : Math.round(el.clientWidth * 0.8);
    el.scrollBy({ left: dir * by, behavior: "smooth" });
  }, []);

  /* Press and drag. The threshold matters: without it, the mousedown that begins a drag also
     swallows an ordinary click, and every link inside a card stops working. */
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let down = false, moved = false, x0 = 0, left0 = 0;

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      down = true; moved = false; x0 = e.clientX; left0 = el.scrollLeft;
    };
    const onMove = (e: PointerEvent) => {
      if (!down) return;
      const dx = e.clientX - x0;
      if (!moved && Math.abs(dx) < 4) return;
      if (!moved) { moved = true; el.setPointerCapture(e.pointerId); el.classList.add("tl-dragging"); }
      el.scrollLeft = left0 - dx;
    };
    const onUp = (e: PointerEvent) => {
      if (moved) {
        el.classList.remove("tl-dragging");
        try { el.releasePointerCapture(e.pointerId); } catch { /* capture may already be gone */ }
      }
      down = false;
    };

    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onUp);
    return () => {
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onUp);
    };
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
    else if (e.key === "Home") { e.preventDefault(); ref.current?.scrollTo({ left: 0, behavior: "smooth" }); }
    else if (e.key === "End") {
      e.preventDefault();
      const el = ref.current;
      el?.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    }
  };

  return (
    <div className="tl">
      <div className="tl-controls">
        <span className="tl-hint">drag, scroll or use the arrow keys</span>
        <div className="tl-btns">
          <button type="button" className="tl-btn" onClick={() => step(-1)} disabled={atStart} aria-label="Earlier entries">
            <ChevronLeft className="h-4 w-4" strokeWidth={1.75} aria-hidden />
          </button>
          <button type="button" className="tl-btn" onClick={() => step(1)} disabled={atEnd} aria-label="Later entries">
            <ChevronRight className="h-4 w-4" strokeWidth={1.75} aria-hidden />
          </button>
        </div>
      </div>

      <div className="tl-frame">
        {/* The fades say "there is more this way" without a scrollbar. They are hidden at each end
            so the mask never sits over content that has nothing beyond it. */}
        <span aria-hidden className={"tl-fade tl-fade-l" + (atStart ? " tl-fade-off" : "")} />
        <span aria-hidden className={"tl-fade tl-fade-r" + (atEnd ? " tl-fade-off" : "")} />

        <div
          ref={ref}
          className="tl-scroll"
          onScroll={measure}
          onKeyDown={onKeyDown}
          tabIndex={0}
          role="group"
          aria-label="Timeline of the market record, newest first"
        >
          {/* NEWEST FIRST. The data is authored oldest-first because that is how the argument
              reads, but a reader who lands here without touching anything should see where the
              story has got to rather than a 2024 entry about somebody else. Reversed at render, so
              the source order stays the readable one. */}
          <ol className="tl-track">
            {[...items].reverse().map((m, i, arr) => (
              <li key={m.when + m.what} className={"tl-item" + (m.us ? " tl-item-us" : "")}>
                {i < arr.length - 1 && <span aria-hidden className="tl-spine" />}

                <span className="tl-when">{m.when}</span>

                <span aria-hidden className="tl-marker">
                  <span className="tl-marker-dot" />
                </span>

                <span aria-hidden className="tl-drop" />

                <h3 className="tl-what">
                  {m.what}
                  {m.us && <span className="tl-badge">ours</span>}
                </h3>
                <p className="tl-detail">{m.detail}</p>
                <p className="tl-source">{m.source}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
